import { AppContextProvider } from "@contexts";
import * as Sentry from "@sentry/react";
import ControlLabelsProvider from "@src/components/control-labels-provider";
import Root from "@src/components/layout";
import { LiveAnnouncerProvider } from "@src/components/live-announcer";
import ToastRegion from "@src/components/toast-container";
import clientRoutes from "@src/modules/client/routes";
import LimitModalContextProvider from "@src/modules/member/context/limit-modal-context";
import memberRoutes from "@src/modules/member/routes";
import { checkForNewVersion } from "@src/utils/version-checker";
import { Suspense, lazy, useEffect } from "react";
import {
  Navigate,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import "../i18n/i18n";
import ErrorComponent from "./components/errors";
import Loading from "./components/loading";
import { clientPathPrefix } from "./modules/client/path";
import { memberPathPrefix } from "./modules/member/path";

declare const Canny: any;

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const ControlsPlayground = import.meta.env.DEV
  ? lazy(() => import("@src/modules/dev/controls-playground"))
  : null;

const devRoutes: RouteObject[] =
  import.meta.env.DEV && ControlsPlayground
    ? [{ path: "dev/controls", element: <ControlsPlayground /> }]
    : [];

const router = sentryCreateBrowserRouter([
  ...devRoutes,
  {
    path: "",
    element: <Root />,
    errorElement: <ErrorComponent />,
    children: [
      {
        path: "",
        element: <Navigate to={memberPathPrefix} />,
      },
      {
        path: memberPathPrefix,
        children: memberRoutes,
      },
      {
        path: clientPathPrefix,
        children: clientRoutes,
      },
    ],
  },
]);

let lastPathname = window.location.pathname;
router.subscribe((state) => {
  if (state.location.pathname !== lastPathname) {
    lastPathname = state.location.pathname;
    checkForNewVersion();
  }
});

function App() {
  useEffect(() => {
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      window.location.reload();
    };
    window.addEventListener("vite:preloadError", handlePreloadError);

    const appID = import.meta.env.VITE_CANNY_APP_ID;
    if (typeof Canny === "function" && appID) {
      console.log("Initializing Canny changelog with appID:", appID);
      try {
        Canny("initChangelog", {
          appID,
          position: "top",
          align: "left",
          theme: "light", // options: light [default], dark, auto
        });
      } catch (error) {
        // Optionally log error to Sentry or console
        console.error("Canny initChangelog failed", error);
      }
    }

    return () => {
      window.removeEventListener("vite:preloadError", handlePreloadError);
      if (typeof Canny === "function") {
        try {
          Canny("closeChangelog");
        } catch (error) {
          console.error("Canny closeChangelog failed", error);
        }
      }
    };
  }, []);

  return (
    <AppContextProvider>
      <LimitModalContextProvider>
        <ControlLabelsProvider>
          <LiveAnnouncerProvider>
            <Suspense fallback={<Loading />}>
              <RouterProvider router={router} />
            </Suspense>
            <ToastRegion />
          </LiveAnnouncerProvider>
        </ControlLabelsProvider>
      </LimitModalContextProvider>
    </AppContextProvider>
  );
}

export default App;
